import { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase/config";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { auth } from "../firebase/config";
import { AuthContext } from "../AuthContext";
import { DEFAULT_LAYOUT } from "../defaultLayout";
import { GUEST_CLASSES } from "../utils/guestDummyData";
// ⭐ Your default classroom layout

export default function Classes() {
  const { isGuest } = useContext(AuthContext);
  const [classes, setClasses] = useState([]);
  const [archivedClasses, setArchivedClasses] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [classStats, setClassStats] = useState({});
  const [weekMode, setWeekMode] = useState("A");
  const [editingClassId, setEditingClassId] = useState(null);
  const [editWeekA, setEditWeekA] = useState("");
  const [editWeekB, setEditWeekB] = useState("");

  // ⭐ Create class + default layout
  const handleAddClass = async (e) => {
    e.preventDefault();

    if (isGuest) {
      alert("Guest mode: Cannot create new classes. This is a demo account.");
      return;
    }

    if (!newClassName.trim()) return;

    try {
      // 1. Create the class
      const classRef = await addDoc(collection(db, "classes"), {
        name: newClassName,
        teacherId: auth.currentUser.uid,
        scheduleWeekA: "",
        scheduleWeekB: "",
        archived: false
      });

      // 2. Create default desk layout for this class
      await setDoc(
        doc(db, "classes", classRef.id, "deskLayouts", "default"),
        {
          desks: DEFAULT_LAYOUT
        }
      );

      setNewClassName("");
      loadClasses();
    } catch (err) {
      console.error("Error adding class:", err);
    }
  };

  // Load all classes for this teacher
  const loadClasses = useCallback(async () => {
    if (isGuest) {
      // Load dummy data for guests
      const activeClasses = GUEST_CLASSES.filter((cls) => !cls.archived);
      const archived = GUEST_CLASSES.filter((cls) => cls.archived);
      setClasses(activeClasses);
      setArchivedClasses(archived);
      
      // Set dummy stats
      const statsMap = {};
      activeClasses.forEach((cls) => {
        statsMap[cls.id] = { positive: 0, negative: 0 };
      });
      setClassStats(statsMap);
      return;
    }

    const q = query(
      collection(db, "classes"),
      where("teacherId", "==", auth.currentUser.uid)
    );

    const snapshot = await getDocs(q);
    const classList = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));

    const activeClasses = classList.filter((cls) => !cls.archived);
    const archived = classList.filter((cls) => cls.archived);

    setClasses(activeClasses);
    setArchivedClasses(archived);

    const statsMap = {};
    await Promise.all(
      activeClasses.map(async (cls) => {
        const eventsRef = collection(db, "classes", cls.id, "events");
        const eventsSnap = await getDocs(eventsRef);
        let positive = 0;
        let negative = 0;
        eventsSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.type === "positive") positive += 1;
          if (data.type === "negative") negative += 1;
        });
        statsMap[cls.id] = { positive, negative };
      })
    );
    setClassStats(statsMap);
  }, [isGuest]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const beginEdit = (cls) => {
    setEditingClassId(cls.id);
    setEditWeekA(cls.scheduleWeekA || "");
    setEditWeekB(cls.scheduleWeekB || "");
  };

  const cancelEdit = () => {
    setEditingClassId(null);
    setEditWeekA("");
    setEditWeekB("");
  };

  const saveSchedule = async (cls) => {
    try {
      await updateDoc(doc(db, "classes", cls.id), {
        scheduleWeekA: editWeekA,
        scheduleWeekB: editWeekB
      });
      setClasses((prev) =>
        prev.map((item) =>
          item.id === cls.id
            ? { ...item, scheduleWeekA: editWeekA, scheduleWeekB: editWeekB }
            : item
        )
      );
      cancelEdit();
    } catch (err) {
      console.error("Error updating schedule:", err);
    }
  };

  const archiveClass = async (cls) => {
    const confirmation = window.prompt(
      `Type the class name to archive: ${cls.name}`
    );

    if (!confirmation || confirmation.trim() !== cls.name) {
      return;
    }

    try {
      await updateDoc(doc(db, "classes", cls.id), {
        archived: true
      });
      setClasses((prev) => prev.filter((item) => item.id !== cls.id));
      setClassStats((prev) => {
        const next = { ...prev };
        delete next[cls.id];
        return next;
      });
      if (editingClassId === cls.id) {
        cancelEdit();
      }
    } catch (err) {
      console.error("Error archiving class:", err);
    }
  };

  const restoreClass = async (cls) => {
    const confirmation = window.prompt(
      `Type the class name to restore: ${cls.name}`
    );

    if (!confirmation || confirmation.trim() !== cls.name) {
      return;
    }

    try {
      await updateDoc(doc(db, "classes", cls.id), {
        archived: false
      });
      setArchivedClasses((prev) => prev.filter((item) => item.id !== cls.id));
      setClasses((prev) => [...prev, { ...cls, archived: false }]);
      loadClasses();
    } catch (err) {
      console.error("Error restoring class:", err);
    }
  };

  return (
    <div>
      <h1>Your Classes</h1>

      <form onSubmit={handleAddClass}>
        <input
          type="text"
          placeholder="Class name (e.g., Year 7 English)"
          value={newClassName}
          onChange={(e) => setNewClassName(e.target.value)}
          style={inputStyles.input}
        />
        <button type="submit" style={buttonStyles.primary}>Add Class</button>
      </form>

      <div style={{ margin: "1rem 0", display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <span style={{ fontWeight: 600 }}>Schedule view:</span>
        <button
          type="button"
          onClick={() => setWeekMode("A")}
          disabled={weekMode === "A"}
          style={
            weekMode === "A"
              ? { ...buttonStyles.secondary, ...buttonStyles.disabled }
              : buttonStyles.secondary
          }
        >
          Week A
        </button>
        <button
          type="button"
          onClick={() => setWeekMode("B")}
          disabled={weekMode === "B"}
          style={
            weekMode === "B"
              ? { ...buttonStyles.secondary, ...buttonStyles.disabled }
              : buttonStyles.secondary
          }
        >
          Week B
        </button>
      </div>

      <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {classes.map((c) => {
          const stats = classStats[c.id] || { positive: 0, negative: 0 };
          const schedule = weekMode === "A" ? c.scheduleWeekA : c.scheduleWeekB;
          const isEditing = editingClassId === c.id;
          return (
            <div key={c.id} style={tileStyle}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
                <Link to={`/classes/${c.id}`} style={tileLinkStyle}>
                  <h3 style={{ margin: 0 }}>{c.name}</h3>
                </Link>
                <Link
                  to={`/classes/${c.id}/seating`}
                  style={{ ...buttonStyles.secondary, textDecoration: "none" }}
                >
                  Seating Plan
                </Link>
              </div>
              <div style={{ marginTop: "0.5rem", display: "flex", gap: "1rem" }}>
                <span style={{ color: "#2e7d32", fontWeight: 600 }}>+{stats.positive}</span>
                <span style={{ color: "#c62828", fontWeight: 600 }}>-{stats.negative}</span>
              </div>
              <div style={{ marginTop: "0.75rem", fontSize: "0.9rem", color: "#555" }}>
                <strong>Week {weekMode}:</strong> {schedule ? schedule : "Not set"}
              </div>
              {isEditing ? (
                <div style={{ marginTop: "0.75rem", display: "grid", gap: "0.5rem" }}>
                  <label style={{ display: "grid", gap: "0.25rem" }}>
                    Week A (e.g., Mon P1, Wed P3)
                    <input
                      type="text"
                      value={editWeekA}
                      onChange={(e) => setEditWeekA(e.target.value)}
                      style={inputStyles.input}
                    />
                  </label>
                  <label style={{ display: "grid", gap: "0.25rem" }}>
                    Week B (e.g., Tue P2, Thu P4)
                    <input
                      type="text"
                      value={editWeekB}
                      onChange={(e) => setEditWeekB(e.target.value)}
                      style={inputStyles.input}
                    />
                  </label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" onClick={() => saveSchedule(c)} style={buttonStyles.primary}>
                      Save
                    </button>
                    <button type="button" onClick={cancelEdit} style={buttonStyles.secondary}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={() => beginEdit(c)}
                    style={buttonStyles.secondary}
                  >
                    Edit schedule
                  </button>
                  <button
                    type="button"
                    onClick={() => archiveClass(c)}
                    style={buttonStyles.danger}
                  >
                    Archive class
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {archivedClasses.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <button
            type="button"
            onClick={() => setShowArchived((prev) => !prev)}
            style={buttonStyles.secondary}
          >
            {showArchived ? "Hide archived classes" : `Show archived classes (${archivedClasses.length})`}
          </button>
          {showArchived && (
            <div style={{ marginTop: "1rem" }}>
              <h2>Archived Classes</h2>
              <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                {archivedClasses.map((c) => (
                  <div key={c.id} style={{ ...tileStyle, opacity: 0.75 }}>
                    <h3 style={{ margin: 0 }}>{c.name}</h3>
                    <div style={{ marginTop: "0.75rem", fontSize: "0.9rem", color: "#555" }}>
                      Archived
                    </div>
                    <div style={{ marginTop: "0.75rem" }}>
                      <button
                        type="button"
                        onClick={() => restoreClass(c)}
                        style={buttonStyles.secondary}
                      >
                        Restore class
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const tileStyle = {
  border: "1px solid #e2e2e2",
  borderRadius: "0.75rem",
  padding: "1rem",
  background: "#fafafa",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.06)",
};

const tileLinkStyle = {
  color: "#1e3a8a",
  textDecoration: "none",
};

const inputStyles = {
  input: {
    padding: "0.65rem 0.75rem",
    borderRadius: "6px",
    border: "1px solid #cfd7e3",
    fontSize: "1rem",
  },
};

const buttonStyles = {
  primary: {
    background: "#4a90e2",
    color: "white",
    border: "none",
    padding: "0.55rem 0.9rem",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  secondary: {
    background: "white",
    color: "#4a90e2",
    border: "1px solid #4a90e2",
    padding: "0.5rem 0.85rem",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
  disabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  danger: {
    background: "#d9534f",
    color: "white",
    border: "none",
    padding: "0.55rem 0.9rem",
    borderRadius: "8px",
    fontWeight: 600,
    cursor: "pointer",
  },
};